import { useCallback, useEffect, useRef, useState } from 'react'
import { HelpButton } from '../components/HelpButton'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import type { Me } from '../lib/me'
import { can, canGallery as canGalleryOf } from '../lib/me'
import { canReachShopAdmin } from '../lib/shop'
import { myLanes } from '../lib/support'
import { supabase } from '../lib/supabase'
import { initialsOf } from '../lib/initials'
import { useHubLang } from '../lib/i18n'
import { CONTESTS, GUIDE_LINKS } from '../lib/hubCopy'
import { GuideLink } from '../components/guides/GuideLink'
import { messageOf } from '../lib/errors'
import {
  DISQUALIFY_REASONS,
  GALLERY_PAGE,
  altFor,
  cancelContest,
  dayLabel,
  disqualify,
  endMonthly,
  enterContest,
  loadContestList,
  loadGallery,
  loadMyContestPhotos,
  reinstate,
  setAnnounce,
  setVoteDays,
  takeVoteBack,
  voteFor,
  withdrawEntry,
  type Contest,
  type DisqualifyReason,
  type GalleryEntry,
  type MyContestPhoto,
} from '../lib/photoContests'
import { AppShell } from '../components/AppShell'
import { RoomThumb } from '../components/RoomThumb'
import { ContestViewer } from '../components/ContestViewer'
import { Dialog } from '../components/Dialog'
import { PhotosBar } from '../components/PhotosBar'
import { TraySection } from '../components/shop/TraySection'
import { TrophyIcon } from '../components/icons'
import { nameOf, phaseLine } from '../components/ContestCard'
import { ConfirmResults, ContestForm, VoteLength } from '../components/PhotoContests'

/**
 * `#/contests` and `#/contests/:id` — photo contests, for everybody (147,
 * CMP-6). The list, one contest's photos, voting, entering a photo, and —
 * for its named judges — disqualifying and confirming the results. Organisers
 * (photo.contest, "Contest organiser") create and change contests here too.
 *
 * ── THE ADMIN IS A SIDE TRAY OF FOLDED SECTIONS (David, 10 Oct) ────────────
 * "The contest management should be in a side tray with collapsible
 * sections." Manage opens the app's tray (Dialog variant="tray"); inside it
 * each job is a TraySection whose header is its summary — judging (open
 * itself when the results wait), details, winners in the feed, the monthly
 * contest — with Cancel at the foot. The list page's tray: a new contest, the
 * monthly contest, and the contests you organise (each links to its own tray
 * with ?manage=1). An afternoon pass had put these in the page; that was a
 * misreading and is gone.
 *
 * ── ENTERING IS A BUTTON, VOTING ALWAYS SHOWS ──────────────────────────────
 * With a hundred photos in, a panel under the gallery is a hundred photos
 * away, so "Submit your photos" opens a dialog from the top of the page. And
 * every photo in a vote shows its vote button — off, with the reason on hover
 * or tap, when it is yours, you judge, or voting is not open — beside a
 * votes-left count, so nobody wonders where voting went.
 */
export function Contests({ me }: { me: Me }) {
  const { id } = useParams()
  const lang = useHubLang()
  const t = CONTESTS[lang]
  const [list, setList] = useState<Contest[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [manageOpen, setManageOpen] = useState(false)
  const [openSec, setOpenSec] = useState<'new' | 'monthly' | 'yours' | null>(null)
  const toggle = (k: 'new' | 'monthly' | 'yours') => setOpenSec((cur) => (cur === k ? null : k))
  const closeTray = () => {
    setManageOpen(false)
    setOpenSec(null)
  }

  async function reload() {
    try {
      setList(await loadContestList())
      setError(null)
    } catch (e) {
      setError(messageOf(e))
    }
  }
  useEffect(() => {
    void reload()
  }, [id])

  const organiser = can(me, 'photo.contest')
  // 152: browsers open the Gallery too (its browse view).
  const canGallery = canGalleryOf(me)
  const tenantId = me.tenants[0]?.id ?? null
  const contest = id && list ? list.find((c) => c.id === id) ?? null : null
  const monthlyRunning = (list ?? []).some((c) => c.seriesId && c.seriesRunning)

  const shell = (children: React.ReactNode) => (
    <AppShell
      current="Photo contests"
      canAdmin={can(me, 'admin.roles')}
      canShop={canReachShopAdmin(me)}
      canModerate={can(me, 'content.moderate')}
      canStarsAdmin={can(me, 'stars.admin')}
      canPhotoRoom={canGallery}
      canAnalytics={can(me, 'analytics.read')}
      canNotices={can(me, 'content.publish')}
      canAudit={can(me, 'audit.read')}
      supportLanes={myLanes(me)}
      userName={me.displayName}
      userInitials={initialsOf(me.displayName)}
      userRole={me.roles[0]?.name ?? 'No role'}
      locale={me.locale}
      onSignOut={() => supabase.auth.signOut()}
    >
      <div className="contests">{children}</div>
    </AppShell>
  )

  if (error) {
    return shell(
      <p className="roles__error" role="alert">
        {t.couldNotLoad} <span className="mono">{error}</span>
      </p>,
    )
  }
  if (list === null) return shell(<p className="queue__empty">{t.loading}</p>)

  if (id) {
    if (!contest) {
      return shell(
        <>
          <Link className="btn btn--link" to="/contests">‹ {t.back}</Link>
          <p className="queue__empty">{t.empty}</p>
        </>,
      )
    }
    return shell(
      <ContestDetail
        key={contest.id}
        contest={contest}
        me={me}
        tenantId={tenantId}
        canGallery={canGallery}
        onChanged={reload}
      />,
    )
  }

  const editable = list.filter((c) => c.isOrganiser && c.phase !== 'final' && c.phase !== 'cancelled')

  return shell(
    <>
      <PhotosBar section="contests" canGallery={canGallery}>
        <HelpButton title={t.howTitle} label={t.howItWorks}>
          <p>{t.lede}</p>
          <p>{t.monthlyHow}</p>
          <p>{t.notShown}</p>
        </HelpButton>
        {/* 353 (HLP-50): the everyday guide's section, beside the page's own ?. */}
        <GuideLink guide="getting-started" section="photos" label={GUIDE_LINKS[lang].photos} />
        {organiser && (
          <button type="button" className="btn btn--quiet btn--sm" aria-haspopup="dialog" onClick={() => setManageOpen(true)}>
            {t.manage}
          </button>
        )}
      </PhotosBar>

      {/* The organiser's tools (David, 10 Oct): a side tray behind Manage,
          in collapsible sections — the page everybody votes on stays photos. */}
      {organiser && (
        <Dialog open={manageOpen} variant="tray" className="contesttray" title={t.manageTitle} onClose={closeTray}>
          <TraySection title={t.newContest} summary={t.newSummary} open={openSec === 'new'} onToggle={() => toggle('new')}>
            <ContestForm
              bare
              contest={null}
              monthly={false}
              tenantId={tenantId}
              me={me}
              onCancel={() => setOpenSec(null)}
              onDone={async () => {
                closeTray()
                await reload()
              }}
            />
          </TraySection>
          <TraySection
            title={t.secMonthly}
            summary={monthlyRunning ? t.monthlyVotes(list.find((c) => c.seriesId && c.seriesRunning)?.seriesVoteDays ?? null) : t.monthlyNotRunning}
            open={openSec === 'monthly'}
            onToggle={() => toggle('monthly')}
          >
            {monthlyRunning ? (
              <p className="photoroom__meta">{t.monthlyRunning}</p>
            ) : (
              <ContestForm
                bare
                contest={null}
                monthly
                tenantId={tenantId}
                me={me}
                onCancel={() => setOpenSec(null)}
                onDone={async () => {
                  closeTray()
                  await reload()
                }}
              />
            )}
          </TraySection>
          {editable.length > 0 && (
            <TraySection
              title={t.secYours}
              summary={t.yoursSummary(editable.length)}
              open={openSec === 'yours'}
              onToggle={() => toggle('yours')}
            >
              <ul className="contests__adminlist">
                {editable.map((c) => (
                  <li key={c.id}>
                    <div>
                      <strong>{nameOf(c, lang)}</strong>
                      <p className="photoroom__meta">{phaseLine(c, lang)}</p>
                    </div>
                    {/* Each contest's own tray is on its page; ?manage opens it. */}
                    <Link className="btn btn--quiet btn--sm" to={`/contests/${c.id}?manage=1`}>
                      {t.manage}
                    </Link>
                  </li>
                ))}
              </ul>
            </TraySection>
          )}
        </Dialog>
      )}

      {list.length === 0 ? (
        <p className="queue__empty">{t.empty}</p>
      ) : (
        <ul className="contestgrid">
          {list.map((c) => (
            <ContestTile key={c.id} c={c} />
          ))}
        </ul>
      )}
    </>,
  )
}

/** One contest in the list: a cover photo, where it is in a word, its dates. */
function ContestTile({ c }: { c: Contest }) {
  const lang = useHubLang()
  const t = CONTESTS[lang]
  const cover = c.phase === 'final' && c.winners[0] ? c.winners[0].path : c.thumbs[0]
  return (
    <li className={`contesttile contesttile--${c.phase}`}>
      <Link className="contesttile__link" to={`/contests/${c.id}`}>
        <span className="contesttile__media">
          {cover ? (
            <RoomThumb path={cover} className="contesttile__img" />
          ) : (
            <span className="contesttile__img contesttile__img--none" aria-hidden="true">
              <TrophyIcon size={28} />
            </span>
          )}
          <span className="contesttile__phase">{t.phaseTag[c.phase]}</span>
        </span>
        <span className="contesttile__body">
          <strong className="contesttile__title">{nameOf(c, lang)}</strong>
          <span className="contesttile__when">{phaseLine(c, lang)}</span>
          <span className="contesttile__meta">
            {c.kind === 'vote' ? t.kindVote : t.kindJudged} · {t.counts(c.entryCount, c.kind === 'vote' ? c.voteTotal : null)}
          </span>
        </span>
      </Link>
    </li>
  )
}

/** The day after a date: the monthly vote opens the day its month ends + 1. */
function dayAfter(d: string): string {
  const [y, m, dd] = d.split('-').map(Number)
  return new Date(Date.UTC(y, m - 1, dd + 1)).toISOString().slice(0, 10)
}

function ContestDetail({
  contest: c,
  me,
  tenantId,
  canGallery,
  onChanged,
}: {
  contest: Contest
  me: Me
  tenantId: string | null
  canGallery: boolean
  onChanged: () => Promise<void>
}) {
  const lang = useHubLang()
  const t = CONTESTS[lang]
  const [entries, setEntries] = useState<GalleryEntry[] | null>(null)
  const [total, setTotal] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [votesLeft, setVotesLeft] = useState(c.votesPerPerson - c.myVotes)
  const [ruling, setRuling] = useState<GalleryEntry | null>(null)
  const [voteDays, setVoteDaysDraft] = useState<number | null>(c.seriesVoteDays)
  const [entering, setEntering] = useState(false)
  const [tip, setTip] = useState<string | null>(null)
  /* David, 11 Oct: a photo opens HERE, large, and the viewer walks the
     contest — not the post page, which walked one post and led to the hub. */
  const [viewing, setViewing] = useState<number | null>(null)
  const loadingMore = useRef(false)
  const live = c.phase !== 'final' && c.phase !== 'cancelled'
  const toConfirm = c.isJudge && c.phase === 'confirming'
  const canManage = live && (c.isOrganiser || c.isJudge)
  // #/contests/:id?manage=1 (the list tray's Manage link) opens the tray.
  const [params] = useSearchParams()
  const [trayOpen, setTrayOpen] = useState(canManage && params.get('manage') === '1')
  type Sec = 'judge' | 'details' | 'feed' | 'monthly'
  const [openSec, setOpenSec] = useState<Sec | null>(toConfirm ? 'judge' : null)
  const toggleSec = (k: Sec) => setOpenSec((cur) => (cur === k ? null : k))

  async function load(offset = 0) {
    try {
      const page = await loadGallery(c.id, offset, GALLERY_PAGE)
      setEntries((cur) => (offset === 0 ? page.entries : [...(cur ?? []), ...page.entries]))
      setTotal(page.total)
    } catch (e) {
      setError(messageOf(e))
    }
  }
  useEffect(() => {
    void load(0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [c.id, c.phase])

  // The viewer asks for the next page near the end; one request at a time.
  const loadMoreForViewer = useCallback(() => {
    if (loadingMore.current || entries === null || entries.length >= total) return
    loadingMore.current = true
    void load(entries.length).finally(() => {
      loadingMore.current = false
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entries, total])

  const voting = c.phase === 'voting' && !c.isJudge
  const isVote = c.kind === 'vote'
  const manyAwards = c.places.filter((p) => p.award === 'judges').length > 1
  const judgeRules = c.isJudge && (c.phase === 'entering' || c.phase === 'voting' || c.phase === 'confirming')
  const voteOpens = dayLabel(dayAfter(c.endsOn), lang)
  const canEnter = c.phase === 'entering' && !c.seriesId

  async function act(key: string, fn: () => Promise<void>) {
    setBusy(key)
    setError(null)
    try {
      await fn()
    } catch (e) {
      setError(messageOf(e))
    } finally {
      setBusy(null)
    }
  }

  async function toggleVote(e: GalleryEntry) {
    await act(e.entryId, async () => {
      const left = e.myVote ? await takeVoteBack(e.entryId) : await voteFor(e.entryId)
      setVotesLeft(left)
      setEntries((cur) => (cur ?? []).map((x) => (x.entryId === e.entryId ? { ...x, myVote: !e.myVote } : x)))
    })
  }

  /** Why this photo's vote button is off — or null when it is on. */
  const voteBlock = (e: GalleryEntry): string | null => {
    if (c.isJudge) return t.voteReasonJudge
    if (e.isMine) return t.voteReasonMine
    if (c.phase === 'upcoming' || c.phase === 'entering') return t.voteReasonNotOpen(voteOpens)
    if (c.phase !== 'voting') return t.voteReasonClosed
    if (!e.myVote && votesLeft <= 0) return t.voteReasonUsed
    return null
  }

  const placeLabelOf = (e: GalleryEntry): string | null =>
    e.place
      ? e.award === 'judges'
        ? t.judgesAward(e.place - c.places.filter((p) => p.award === 'place').length, manyAwards)
        : t.placeName(e.place)
      : null

  /** The viewer's line: what the tile says in its flag and its meta, joined. */
  const viewerMeta = (e: GalleryEntry): string | null => {
    const showName = !!(e.entrantName && !e.isMine)
    const bits = [
      placeLabelOf(e),
      e.isMine ? t.yourPhoto : null,
      showName ? t.postedBy(e.entrantName as string) : null,
      e.votes !== null ? t.votes(e.votes) : null,
    ].filter(Boolean)
    return bits.length ? bits.join(' · ') : null
  }

  /** The vote button and "take it back" — ONE drawing, used by the tile and
   *  the viewer, so the reasons a vote is off cannot differ between them. */
  const voteControls = (e: GalleryEntry, where: 'tile' | 'viewer') => {
    const showVote = isVote && live && !e.disqualified
    const blocked = showVote ? voteBlock(e) : null
    const tipKey = `${where}-${e.entryId}`
    return (
      <>
        {showVote && (
          /* Always drawn in a vote (David, 10 Oct): off, with the
             reason on hover — or on a tap, where there is no
             hover — when it is yours, you judge, or voting is
             not open. aria-disabled rather than disabled, so the
             reason can be reached and read. */
          <span
            className={`votebtn${blocked ? ' votebtn--off' : ''}${tip === tipKey ? ' is-tipped' : ''}`}
            data-tip={blocked ?? undefined}
          >
            <button
              type="button"
              className={e.myVote ? 'btn btn--primary btn--sm photoroom__vote' : 'btn btn--quiet btn--sm photoroom__vote'}
              aria-pressed={e.myVote}
              aria-disabled={blocked ? true : undefined}
              aria-describedby={blocked ? `why-${tipKey}` : undefined}
              disabled={busy === e.entryId}
              onClick={() => {
                if (blocked) {
                  setTip(tip === tipKey ? null : tipKey)
                  return
                }
                void toggleVote(e)
              }}
              onBlur={() => setTip(null)}
            >
              {e.myVote ? t.youVoted : t.voteFor}
            </button>
            {blocked && (
              <span id={`why-${tipKey}`} role="tooltip" className="votebtn__tip">
                {blocked}
              </span>
            )}
          </span>
        )}
        {voting && e.myVote && (
          <button type="button" className="btn btn--link btn--sm" disabled={busy === e.entryId} onClick={() => void toggleVote(e)}>
            {t.takeBack}
          </button>
        )}
      </>
    )
  }

  const gallery = (
    <>
      {entries === null && !error && <p className="queue__empty">{t.loading}</p>}
      {entries !== null && entries.length === 0 && <p className="queue__empty">{t.noPhotos}</p>}
      {entries !== null && entries.length > 0 && (
        <>
          <ul className="photoroom photoroom--contest">
            {entries.map((e, i) => {
              const placeLabel = placeLabelOf(e)
              const showVote = isVote && live && !e.disqualified
              const canRule = judgeRules && !e.isMine && !e.disqualified
              const canReinstate = c.isJudge && e.disqualified && !e.isMine && (c.phase === 'entering' || c.phase === 'voting')
              const canWithdraw = c.phase === 'entering' && e.isMine && !c.seriesId
              const showName = !!(e.entrantName && !e.isMine)
              const hasBody = showVote || canRule || canReinstate || canWithdraw || showName || e.votes !== null || e.disqualified
              const img = <RoomThumb path={e.path} alt={altFor(e, lang)} className="photoroom__img" />
              return (
                <li key={e.entryId} className={e.place ? 'photoroom__item photoroom__item--chosen' : 'photoroom__item'}>
                  <div className="photoroom__media">
                    {/* The photo opens the CONTEST viewer (David, 11 Oct): large,
                        through every photo, with the vote. It used to open its
                        post, whose viewer walked one post and whose page led
                        back to the hub. The post is one link inside the viewer. */}
                    <button
                      type="button"
                      className="photoroom__open"
                      aria-haspopup="dialog"
                      aria-label={t.viewPhoto(i + 1, Math.max(total, entries.length))}
                      onClick={() => setViewing(i)}
                    >
                      {img}
                    </button>
                    {(placeLabel || e.isMine) && (
                      <span className="photoroom__flags">
                        {placeLabel && <span className="photoroom__flag photoroom__flag--win">{placeLabel}</span>}
                        {e.isMine && <span className="photoroom__flag">{t.yourPhoto}</span>}
                      </span>
                    )}
                  </div>
                  {hasBody && (
                    <div className="photoroom__body">
                      {(showName || e.votes !== null) && (
                        <p className="photoroom__meta">
                          {[showName ? t.postedBy(e.entrantName as string) : null, e.votes !== null ? t.votes(e.votes) : null]
                            .filter(Boolean)
                            .join(' · ')}
                        </p>
                      )}
                      {e.disqualified && (
                        <p className="photoroom__notice photoroom__notice--warn">
                          {t.disqualified}
                          {e.disqualifyReason ? ` — ${t.reasons[e.disqualifyReason]}` : ''}
                          {e.disqualifyNote ? ` (${e.disqualifyNote})` : ''}
                        </p>
                      )}
                      {(showVote || canRule || canReinstate || canWithdraw) && (
                        <div className="photoroom__actions">
                          {voteControls(e, 'tile')}
                          {canRule && (
                            <button type="button" className="btn btn--link btn--sm" onClick={() => setRuling(e)}>
                              {t.disqualify}
                            </button>
                          )}
                          {canReinstate && (
                            <button
                              type="button"
                              className="btn btn--link btn--sm"
                              disabled={busy === e.entryId}
                              onClick={() => void act(e.entryId, async () => { await reinstate(e.entryId); await load(0) })}
                            >
                              {t.reinstate}
                            </button>
                          )}
                          {canWithdraw && (
                            <button
                              type="button"
                              className="btn btn--link btn--sm"
                              disabled={busy === e.entryId}
                              onClick={() => void act(e.entryId, async () => { await withdrawEntry(c.id, e.path); await onChanged(); await load(0) })}
                            >
                              {t.withdrawButton}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </li>
              )
            })}
          </ul>
          {entries.length < total && (
            <button type="button" className="btn btn--quiet btn--sm photoroom__more" onClick={() => void load(entries.length)}>
              {t.showMore}
            </button>
          )}
          {viewing !== null && entries[viewing] && (
            <ContestViewer
              entries={entries}
              total={total}
              index={viewing}
              onIndex={setViewing}
              onClose={() => setViewing(null)}
              onNeedMore={loadMoreForViewer}
              meta={viewerMeta}
              voteControls={(e) => voteControls(e, 'viewer')}
            />
          )}
        </>
      )}
    </>
  )

  // The votes-left line: always there in a vote, saying what is true now.
  const voteLine = !isVote || !live
    ? null
    : c.isJudge
      ? t.voteReasonJudge
      : c.phase === 'voting'
        ? votesLeft > 0 ? t.votesStatus(votesLeft, c.votesPerPerson) : t.voteReasonUsed
        : c.phase === 'confirming'
          ? t.voteReasonClosed
          : t.votesOpenOn(voteOpens, c.votesPerPerson)

  return (
    <>
      <PhotosBar section="contests" canGallery={canGallery} />
      <div className="contests__top">
        <Link className="btn btn--link" to="/contests">‹ {t.back}</Link>
        <span className="contests__topactions">
          {canEnter && (
            <button type="button" className="btn btn--primary btn--sm" aria-haspopup="dialog" onClick={() => setEntering(true)}>
              {t.submitPhotos}
            </button>
          )}
          {canManage && (
            <button
              type="button"
              className={toConfirm ? 'btn btn--primary btn--sm' : 'btn btn--quiet btn--sm'}
              aria-haspopup="dialog"
              onClick={() => setTrayOpen(true)}
            >
              {toConfirm ? t.confirmCta : t.manage}
            </button>
          )}
        </span>
      </div>
      {/* CMP-8 (David, 10 Oct): the photos to vote on first; the
          explanations behind "Show details". */}
      <ContestCardHeader c={c} />
      {error && (
        <p className="roles__error" role="alert">
          {error}
        </p>
      )}

      {c.phase === 'final' && c.announcementId && (
        <p className="photoroom__meta">
          <Link to={`/item/${c.announcementId}`}>{t.seePost}</Link>
        </p>
      )}

      {(voteLine || (entries !== null && entries.length > 0)) && (
        <p className="photoroom__count">
          {entries !== null && entries.length > 0 ? t.shown(entries.length, total) : null}
          {voteLine && (
            <strong className="contests__votesleft">
              {entries !== null && entries.length > 0 ? ' · ' : ''}
              {voteLine}
            </strong>
          )}
        </p>
      )}
      {gallery}

      {/* Organising and judging (David, 10 Oct): a side tray behind Manage, in
          collapsible sections. The judge's section opens itself when the
          results are waiting, and the button then says "Confirm the results". */}
      {canManage && (
        <Dialog open={trayOpen} variant="tray" className="contesttray" title={t.manageContest} onClose={() => setTrayOpen(false)}>
          <p className="contesttray__name">
            <strong>{nameOf(c, lang)}</strong>
            <span className="photoroom__meta">{phaseLine(c, lang)}</span>
          </p>
          {error && (
            <p className="roles__error" role="alert">
              {error}
            </p>
          )}
          {c.isJudge && (
            <TraySection
              title={toConfirm ? t.confirmCta : t.judgeHeading}
              summary={t.judgeSummary(toConfirm)}
              open={openSec === 'judge'}
              onToggle={() => toggleSec('judge')}
            >
              <div className="contests__judge">
                <p className="photoroom__meta">{t.judgeLede}</p>
                {toConfirm && <ConfirmResults contest={c} onDone={onChanged} />}
              </div>
            </TraySection>
          )}
          {c.isOrganiser && !c.seriesId && (
            <TraySection title={t.secDetails} summary={t.detailsSummary} open={openSec === 'details'} onToggle={() => toggleSec('details')}>
              <ContestForm
                bare
                contest={c}
                monthly={false}
                tenantId={tenantId}
                me={me}
                onCancel={() => setOpenSec(null)}
                onDone={async () => {
                  setOpenSec(null)
                  await onChanged()
                }}
              />
            </TraySection>
          )}
          {c.isOrganiser && (
            <TraySection
              title={t.secFeed}
              summary={c.announceInFeed ? t.feedOn : t.feedOff}
              open={openSec === 'feed'}
              onToggle={() => toggleSec('feed')}
            >
              {c.seriesId ? (
                <p className="photoroom__meta">{t.monthlyAnnounces}</p>
              ) : (
                <label className="contests__check">
                  <input
                    type="checkbox"
                    checked={c.announceInFeed}
                    disabled={busy === 'announce'}
                    onChange={(ev) => void act('announce', async () => { await setAnnounce(c.id, ev.target.checked); await onChanged() })}
                  />
                  <span>
                    {t.announceInFeed}
                    <span className="contests__hint">{t.announceHint}</span>
                  </span>
                </label>
              )}
            </TraySection>
          )}
          {c.isOrganiser && c.seriesId && c.seriesRunning && (
            <TraySection
              title={t.secMonthly}
              summary={t.monthlyVotes(c.seriesVoteDays)}
              open={openSec === 'monthly'}
              onToggle={() => toggleSec('monthly')}
            >
              <div className="contests__monthly">
                <VoteLength value={voteDays} onChange={setVoteDaysDraft} />
                <div className="roomviewer__row">
                  <button
                    type="button"
                    className="btn btn--quiet btn--sm"
                    disabled={busy === 'days' || voteDays === c.seriesVoteDays}
                    onClick={() => void act('days', async () => { await setVoteDays(c.seriesId as string, voteDays); await onChanged() })}
                  >
                    {t.saveVoteDays}
                  </button>
                  <button
                    type="button"
                    className="btn btn--link btn--sm contests__danger"
                    onClick={() => {
                      if (!window.confirm(t.confirmEndMonthly)) return
                      void act('end', async () => { await endMonthly(c.seriesId as string); await onChanged() })
                    }}
                  >
                    {t.endMonthly}
                  </button>
                </div>
              </div>
            </TraySection>
          )}
          {c.isOrganiser && (
            <div className="contesttray__foot">
              <button
                type="button"
                className="btn btn--link btn--sm contests__danger"
                disabled={busy === 'cancel'}
                onClick={() => {
                  if (!window.confirm(t.confirmCancel)) return
                  void act('cancel', async () => {
                    await cancelContest(c.id)
                    setTrayOpen(false)
                    await onChanged()
                  })
                }}
              >
                {t.cancelContest}
              </button>
            </div>
          )}
        </Dialog>
      )}

      {canEnter && (
        <Dialog open={entering} title={t.submitPhotos} onClose={() => setEntering(false)}>
          <EnterPanel c={c} onChanged={async () => { await onChanged(); await load(0) }} />
        </Dialog>
      )}

      <Dialog open={ruling !== null} title={t.disqualify} onClose={() => setRuling(null)}>
        {ruling && (
          <DisqualifyForm
            entry={ruling}
            onClose={() => setRuling(null)}
            onDone={async () => {
              setRuling(null)
              await load(0)
              await onChanged()
            }}
          />
        )}
      </Dialog>
    </>
  )
}

/** The contest's own header on its page: the hub card's facts, without its button. */
function ContestCardHeader({ c }: { c: Contest }) {
  const lang = useHubLang()
  const t = CONTESTS[lang]
  const first = c.places.find((p) => p.award === 'place')
  const award = c.places.find((p) => p.award === 'judges')
  return (
    <header className="contests__header">
      <h2 className="contests__title">{nameOf(c, lang)}</h2>
      {c.objective && <p className="contestcard__objective">{c.objective}</p>}
      <div className="contestcard__when">
        {phaseLine(c, lang)}{' '}
        <HelpButton title={t.detailsTitle} label={t.showDetails}>
          <p className="photoroom__meta">
            <span className="photoroom__badge">{c.kind === 'vote' ? t.kindVote : t.kindJudged}</span>{' '}
            {c.seriesId && <span className="photoroom__badge">{t.monthlyTag}</span>}
          </p>
          {/* This contest's own kind (David, 12 Oct): the general overview is
              "How it works" on the contests page, not every contest's details. */}
          <p>{t.aboutContest(Boolean(c.seriesId), c.kind === 'vote')}</p>
          {c.seriesId && c.phase === 'entering' && <p>{t.monthlyHow}</p>}
          {(c.phase === 'entering' || c.phase === 'voting') && c.kind === 'vote' && <p>{t.notShown}</p>}
          <p>
            {first && first.stars > 0 ? t.prize(first.stars, c.prize) : c.prize ?? ''}
            {award && award.stars > 0 ? ` · ${t.judgesAwardPrize(award.stars)}` : ''}
          </p>
          <p>{t.counts(c.entryCount, c.kind === 'vote' ? c.voteTotal : null)}</p>
          {c.judges.length > 0 && <p>{t.judgedBy(c.judges.map((j) => j.name).join(', '))}</p>}
          {c.isJudge && c.phase !== 'final' && c.phase !== 'cancelled' && <p>{t.judgeLede}</p>}
        </HelpButton>
      </div>
    </header>
  )
}

function EnterPanel({ c, onChanged }: { c: Contest; onChanged: () => Promise<void> }) {
  const lang = useHubLang()
  const t = CONTESTS[lang]
  const [photos, setPhotos] = useState<MyContestPhoto[] | null>(null)
  const [total, setTotal] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)

  async function load(offset = 0) {
    try {
      const page = await loadMyContestPhotos(c.id, offset, GALLERY_PAGE)
      setPhotos((cur) => (offset === 0 ? page.photos : [...(cur ?? []), ...page.photos]))
      setTotal(page.total)
    } catch (e) {
      setError(messageOf(e))
    }
  }
  useEffect(() => {
    void load(0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [c.id])

  const enteredNow = (photos ?? []).filter((p) => p.entered).length

  async function toggle(p: MyContestPhoto) {
    setBusy(p.path)
    setError(null)
    try {
      if (p.entered) await withdrawEntry(c.id, p.path)
      else await enterContest(c.id, p.path)
      setPhotos((cur) => (cur ?? []).map((x) => (x.path === p.path ? { ...x, entered: !p.entered } : x)))
      await onChanged()
    } catch (e) {
      setError(messageOf(e))
    } finally {
      setBusy(null)
    }
  }

  return (
    <section className="contests__enter">
      <p className="photoroom__meta">{t.enterLede(c.maxEntries ?? 1, dayLabel(c.photosSince ?? c.startsOn, lang))}</p>
      {c.maxEntries !== null && <p className="photoroom__meta">{t.enteredCount(Math.max(enteredNow, c.myEntries), c.maxEntries)}</p>}
      {error && (
        <p className="roles__error" role="alert">
          {error}
        </p>
      )}
      {photos !== null && photos.length === 0 && <p className="queue__empty">{t.enterNone}</p>}
      {photos !== null && photos.length > 0 && (
        <>
          <ul className="contests__mine">
            {photos.map((p) => (
              <li key={p.path} className={p.entered ? 'contests__mineitem contests__mineitem--in' : 'contests__mineitem'}>
                <RoomThumb path={p.path} alt={p.caption ?? ''} className="contests__thumb" />
                <span className="photoroom__meta">{p.caption ?? dayLabel(p.postedAt.slice(0, 10), lang)}</span>
                <button
                  type="button"
                  className={p.entered ? 'btn btn--link' : 'btn btn--quiet btn--sm'}
                  disabled={busy === p.path}
                  onClick={() => void toggle(p)}
                >
                  {p.entered ? t.withdrawButton : t.enterButton}
                </button>
                {p.entered && <span className="photoroom__badge">{t.entered}</span>}
              </li>
            ))}
          </ul>
          {photos.length < total && (
            <button type="button" className="btn btn--quiet btn--sm" onClick={() => void load(photos.length)}>
              {t.showMore}
            </button>
          )}
        </>
      )}
    </section>
  )
}

function DisqualifyForm({ entry, onClose, onDone }: { entry: GalleryEntry; onClose: () => void; onDone: () => Promise<void> }) {
  const lang = useHubLang()
  const t = CONTESTS[lang]
  const [reason, setReason] = useState<DisqualifyReason>('off_objective')
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  return (
    <form
      className="contests__form contests__rule"
      onSubmit={async (ev) => {
        ev.preventDefault()
        if (reason === 'other' && !note.trim()) {
          setError(t.noteRequired)
          return
        }
        setBusy(true)
        setError(null)
        try {
          await disqualify(entry.entryId, reason, note.trim() || null)
          await onDone()
        } catch (e) {
          setError(messageOf(e))
        } finally {
          setBusy(false)
        }
      }}
    >
      <RoomThumb path={entry.path} className="contests__thumb" />
      <label>
        {t.reason}
        <select value={reason} onChange={(e) => setReason(e.target.value as DisqualifyReason)}>
          {DISQUALIFY_REASONS.map((r) => (
            <option key={r} value={r}>
              {t.reasons[r]}
            </option>
          ))}
        </select>
      </label>
      <label>
        {t.note}
        <input value={note} maxLength={300} onChange={(e) => setNote(e.target.value)} />
      </label>
      <p className="photoroom__meta">{t.disqualifyNote}</p>
      {error && (
        <p className="roles__error" role="alert">
          {error}
        </p>
      )}
      <div className="roomviewer__row">
        <button type="submit" className="btn btn--primary btn--sm" disabled={busy}>
          {t.disqualifyConfirm}
        </button>
        <button type="button" className="btn btn--quiet btn--sm" onClick={onClose}>
          {t.cancel}
        </button>
      </div>
    </form>
  )
}

